import React, { FunctionComponent } from 'react';
import { getImageUrl } from '../../../../utils/navigation';
import { formatCurrency } from '../../../../utils/wallets';
import './BingoCardBox.scss';
import { BingoData } from '../BingoCard';
import { Heading } from '../../../shared/Heading';
import LazyLoadedImage from '../../../Media/LazyLoadedImage';
import { BingoProfileIcon, BingoJackpotIcon, PrizeIcon, CardCostIcon } from '../../../shared/SVG/Icons/';
import { ballImages, formatTimeToStart } from '../shared';

interface BingoCardBoxProps {
  bingo: BingoData;
  onPlay: () => void;
}

export const BingoCardBox: FunctionComponent<BingoCardBoxProps> = ({ bingo, onPlay }): JSX.Element => {
  const {
    title = 'Bingo Game Name',
    jackpot,
    prize,
    cardCost,
    players = 0,
    number_of_balls,
    img,
    imgBox,
    roomID,
    hasRealTimeData,
    timeToStart = -1,
  } = bingo;

  const imageUrl = getImageUrl(imgBox?.filename_disk || img?.filename_disk);
  const formattedJackpot = jackpot ? formatCurrency(parseFloat(jackpot))?.replace(/\s/g, '') : '-';
  const formattedPrize = prize ? formatCurrency(parseFloat(prize))?.replace(/\s/g, '') : '-';
  const formattedCardCost = `£${cardCost ? parseFloat(cardCost).toFixed(2) : '0.00'}`;

  const handlePlay = (event: React.MouseEvent): void => {
    event.stopPropagation();

    if (roomID) {
      onPlay();
    }
  };

  return (
    <div className="bingo-card-box">
      <div className="bingo-card-box__image-container">
        {imageUrl ? (
          <LazyLoadedImage src={imageUrl} alt={title} className="bingo-card-box__image" />
        ) : (
          <div className="bingo-card-box__placeholder" />
        )}
        {timeToStart > 0 && <div className="bingo-card-box__time-container">{formatTimeToStart(timeToStart)}</div>}
      </div>
      <div className="bingo-card-box__content">
        <div className="bingo-card-box__row">
          <div className="bingo-card-box__left-section">
            <Heading className="bingo-card-box__title" noMargin level={5}>
              {title}
            </Heading>
            {!hasRealTimeData &&
              number_of_balls &&
              (ballImages[number_of_balls] ? (
                <LazyLoadedImage
                  src={ballImages[number_of_balls]}
                  alt={`${number_of_balls} Ball`}
                  className="bingo-card-box__balls"
                />
              ) : (
                <div className="bingo-card-box__balls-text">{number_of_balls} Ball</div>
              ))}
          </div>
          <div className="bingo-card-box__right-section">
            <div className="bingo-card-box__ball-section">
              {hasRealTimeData &&
                number_of_balls &&
                (ballImages[number_of_balls] ? (
                  <LazyLoadedImage
                    src={ballImages[number_of_balls]}
                    alt={`${number_of_balls} Ball`}
                    className="bingo-card-box__balls"
                  />
                ) : (
                  <div className="bingo-card-box__balls-text">{number_of_balls} Ball</div>
                ))}
            </div>
            {!hasRealTimeData && (
              <button className="bingo-card-box__play-btn" onClick={handlePlay}>
                {bingo.ctaText}
              </button>
            )}
          </div>
        </div>
        {hasRealTimeData && (
          <>
            <div className="bingo-card-box__realtime-desktop">
              <div className="bingo-card-box__row bingo-card-box__row--game-info">
                <div className="bingo-card-box__left-section">
                  <div className="bingo-card-box__money-info">
                    <div className="bingo-card-box__jackpot">
                      <BingoJackpotIcon className="bingo-card-box__jackpot-icon" />
                      <span className="bingo-card-box__stat-value">{formattedJackpot}</span>
                    </div>
                    <div className="bingo-card-box__prize">
                      <PrizeIcon className="bingo-card-box__prize-icon" />
                      <span className="bingo-card-box__stat-value">{formattedPrize}</span>
                    </div>
                    <div className="bingo-card-box__bet-range">
                      <CardCostIcon className="bingo-card-box__card-cost-icon" />
                      <span className="bingo-card-box__card-cost">{formattedCardCost}</span>
                      <BingoProfileIcon className="bingo-card-box__player-icon" aria-hidden="true" />
                      <span className="bingo-card-box__stat-value">{players || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bingo-card-box__right-section">
                  {jackpot && (
                    <button className="bingo-card-box__play-btn" onClick={handlePlay}>
                      {bingo.ctaText}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bingo-card-box__realtime-mobile">
              <div className="bingo-card-box__stats-grid">
                <div className="bingo-card-box__stats-column">
                  <div className="bingo-card-box__players">
                    <BingoProfileIcon className="bingo-card-box__player-icon" aria-hidden="true" />
                    <span className="bingo-card-box__stat-value">{players || 0}</span>
                  </div>
                  <div className="bingo-card-box__prize">
                    <PrizeIcon className="bingo-card-box__prize-icon" />
                    <span className="bingo-card-box__stat-value">{formattedPrize}</span>
                  </div>
                </div>
                <div className="bingo-card-box__stats-column">
                  <div className="bingo-card-box__bet-range">
                    <CardCostIcon className="bingo-card-box__card-cost-icon" />
                    <span className="bingo-card-box__card-cost">{formattedCardCost}</span>
                  </div>
                  <div className="bingo-card-box__jackpot">
                    <BingoJackpotIcon className="bingo-card-box__jackpot-icon" />
                    <span className="bingo-card-box__stat-value">{formattedJackpot}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
